// src/hooks/useOrgSettings.ts
// Loads branding/display settings for the active org. Falls back to defaults if
// the columns haven't been added yet (run supabase_admin_onboarding.sql first).
import { useEffect, useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { PLATFORM_DEFAULT_APP_DISPLAY_NAME } from "@/config/productDisplay";
import { requireSupabase } from "@/lib/supabase";

/** Catalog row for Opportunity + Manager. Sourced from Ops Deal Types. */
export type ProductTypeEntry = {
  tag: string;
  label: string;
  description: string;
  /** Deal Type metadata: later deals share a serial, not the original sale. */
  followOn?: boolean;
};

export type OrgSettings = {
  /** `organizations.name` — workspace display name when brand_name is unset. */
  organizationName: string;
  brandName: string;
  appDisplayName: string;
  logoUrl: string | null;
  /** Hex color (#RRGGBB) for primary brand elements (nav bar, CTA buttons). */
  colorPrimary: string | null;
  /** Hex color (#RRGGBB) for secondary elements (badges, links, accents). */
  colorSecondary: string | null;
  /** Short tagline shown after brand name in the app header (max 20 chars). Null = hidden. */
  tagline: string | null;
  /**
   * Opportunity Engine: default commission % (0–100) prefilled into the
   * pricing form for new opportunities. Null when the column isn't set
   * (or the migration hasn't been applied in this environment yet).
   */
  defaultCommissionPct: number | null;
  /** Opportunity Engine: default margin % (0–100). See above. */
  defaultMarginPct: number | null;
  /** Proposal PDF contact defaults (org admin). */
  proposalContactRow: {
    proposal_contact_org_name: string | null;
    proposal_contact_person_name: string | null;
    proposal_contact_address: string | null;
    proposal_contact_phone: string | null;
    proposal_contact_email: string | null;
  };
};

const DEFAULTS: OrgSettings = {
  organizationName: "",
  brandName: "",
  appDisplayName: PLATFORM_DEFAULT_APP_DISPLAY_NAME,
  logoUrl: null,
  colorPrimary: null,
  colorSecondary: null,
  tagline: null,
  defaultCommissionPct: null,
  defaultMarginPct: null,
  proposalContactRow: {
    proposal_contact_org_name: null,
    proposal_contact_person_name: null,
    proposal_contact_address: null,
    proposal_contact_phone: null,
    proposal_contact_email: null,
  },
};

export function useOrgSettings(): OrgSettings {
  const scopedOrganizationId = useScopedOrganizationId();
  const [settings, setSettings] = useState<OrgSettings>(DEFAULTS);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const orgId = scopedOrganizationId;
    if (!orgId) return;

    let cancelled = false;
    (async () => {
      try {
        const sb = requireSupabase();
        // The Opportunity Engine columns (default_commission_pct,
        // default_margin_pct) are appended to the SELECT but the catch
        // below tolerates the "column does not exist" case so orgs that
        // haven't applied the Opportunity Engine migration yet keep
        // working — they just see `null` defaults in the pricing form.
        const { data } = await sb
          .from("organizations")
          .select(
            "name, brand_name, app_display_name, logo_url, color_primary, color_secondary, tagline, default_commission_pct, default_margin_pct, proposal_contact_org_name, proposal_contact_person_name, proposal_contact_address, proposal_contact_phone, proposal_contact_email",
          )
          .eq("id", orgId)
          .maybeSingle();
        if (cancelled || !data) return;
        const rawCommission = (data as any).default_commission_pct;
        const rawMargin = (data as any).default_margin_pct;
        const row = data as Record<string, unknown>;
        setSettings({
          organizationName: (data as any).name?.trim() || DEFAULTS.organizationName,
          brandName: (data as any).brand_name || DEFAULTS.brandName,
          appDisplayName: (data as any).app_display_name || DEFAULTS.appDisplayName,
          logoUrl: (data as any).logo_url || null,
          colorPrimary: (data as any).color_primary || null,
          colorSecondary: (data as any).color_secondary || null,
          tagline: (data as any).tagline ?? null,
          defaultCommissionPct: typeof rawCommission === "number" ? rawCommission : null,
          defaultMarginPct: typeof rawMargin === "number" ? rawMargin : null,
          proposalContactRow: {
            proposal_contact_org_name:
              typeof row.proposal_contact_org_name === "string"
                ? row.proposal_contact_org_name
                : null,
            proposal_contact_person_name:
              typeof row.proposal_contact_person_name === "string"
                ? row.proposal_contact_person_name
                : null,
            proposal_contact_address:
              typeof row.proposal_contact_address === "string"
                ? row.proposal_contact_address
                : null,
            proposal_contact_phone:
              typeof row.proposal_contact_phone === "string" ? row.proposal_contact_phone : null,
            proposal_contact_email:
              typeof row.proposal_contact_email === "string" ? row.proposal_contact_email : null,
          },
        });
      } catch {
        // network/permission error — leave defaults in place
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scopedOrganizationId, refreshTick]);

  // Listen for save events from the admin panel to re-fetch without a page reload
  useEffect(() => {
    const onSaved = () => setRefreshTick((t) => t + 1);
    window.addEventListener("org-settings-saved", onSaved);
    return () => window.removeEventListener("org-settings-saved", onSaved);
  }, []);

  // Inject org brand colors as CSS custom properties so any element can reference
  // var(--color-primary) / var(--color-secondary) without rebuilding Tailwind.
  useEffect(() => {
    const root = document.documentElement;
    if (settings.colorPrimary) {
      root.style.setProperty("--color-primary", settings.colorPrimary);
    } else {
      root.style.removeProperty("--color-primary"); // fall back to index.css default
    }
    if (settings.colorSecondary) {
      root.style.setProperty("--color-secondary", settings.colorSecondary);
    } else {
      root.style.removeProperty("--color-secondary");
    }
  }, [settings.colorPrimary, settings.colorSecondary]);

  useEffect(() => {
    const name = settings.appDisplayName.trim() || PLATFORM_DEFAULT_APP_DISPLAY_NAME;
    document.title = name;
  }, [settings.appDisplayName]);

  return settings;
}
